import keys from 'client/components/txtkeys/subscriptionKeys'
import { QuotaTypes } from 'client/Interfaces/Subscription'
import {
  ISubscriptionFeatureItem,
  ISubscriptionFeatureList,
  SubscriptionFeatureCheckboxType,
  SubscriptionFeatureType,
} from 'client/Interfaces/Subscription/ISubscriptionFeatureList'
import { l } from 'client/Other/Translation'
import { setBaseClass } from 'client/utils'
import { groupBy } from 'lodash'
import * as React from 'react'
import CheckmarkCross from 'static/images/premiumPlans/checkmark/checkmark_cross.svg'
import CheckmarkDash from 'static/images/premiumPlans/checkmark/checkmark_dash.svg'
import CheckmarkDeepSeaGreen from 'static/images/premiumPlans/checkmark/checkmark_deepSeaGreen.svg'
import CheckmarkElectricGreen from 'static/images/premiumPlans/checkmark/checkmark_electricGreen.svg'
import CheckmarkGray90 from 'static/images/premiumPlans/checkmark/checkmark_gray90.svg'
import CheckmarkGreen from 'static/images/premiumPlans/checkmark/checkmark_green.svg'

import { ISubscriptionPremiumPlanOptionsPropsInterface } from '../IPremiumInterface'
import './FeatureList.scss'
import InvoiceQuota from './InvoiceQuota'

const cls = setBaseClass('SubscriptionFeatureList')

type TFeaturesSection = Pick<
  ISubscriptionPremiumPlanOptionsPropsInterface,
  'selectedPlanName' | 'isCombinedPlan' | 'onSelectCombinedPlan' | 'selectableAvailablePlans'
>
interface IFeaturesSection extends TFeaturesSection {
  classProps: string[]
  features: ISubscriptionFeatureItem[]
  billingCycle: ISubscriptionFeatureList['billingCycle']
  allPlans
  processingPlanName?: string
}

const FeatureCheckmark = ({ feature }: { feature: ISubscriptionFeatureItem }) => {
  const checkmark = {
    [SubscriptionFeatureCheckboxType.RED]: CheckmarkCross,
    [SubscriptionFeatureCheckboxType.DEEP_SEA_GREEN]: CheckmarkDeepSeaGreen,
    [SubscriptionFeatureCheckboxType.GREEN]: CheckmarkGreen,
    [SubscriptionFeatureCheckboxType.ELECTRIC_GREEN]: CheckmarkElectricGreen,
    [SubscriptionFeatureCheckboxType.DISABLED]: CheckmarkGray90,
    [SubscriptionFeatureCheckboxType.DASH]: CheckmarkDash,
  }

  return (
    <img
      className={cls({
        __checkmark: true,
        ...{
          '--checkmark-red': feature.checkboxStyle === SubscriptionFeatureCheckboxType.RED,
          '--checkmark-green': feature.checkboxStyle === SubscriptionFeatureCheckboxType.GREEN,
          '--checkmark-deepSeaGreen': feature.checkboxStyle === SubscriptionFeatureCheckboxType.DEEP_SEA_GREEN,
          '--checkmark-electricGreen': feature.checkboxStyle === SubscriptionFeatureCheckboxType.ELECTRIC_GREEN,
          '--checkmark-disabled': feature.checkboxStyle === SubscriptionFeatureCheckboxType.DISABLED,
          '--checkmark-dash': feature.checkboxStyle === SubscriptionFeatureCheckboxType.DASH,
        },
      })}
      src={checkmark[feature.checkboxStyle]}
    />
  )
}

const FeaturesSection = (props: IFeaturesSection) => {
  const {
    selectedPlanName,
    features,
    classProps,
    billingCycle,
    isCombinedPlan,
    onSelectCombinedPlan,
    selectableAvailablePlans,
    allPlans,
    processingPlanName,
  } = props

  const isAgerasPlan = features && features.some((item) => item.id === 'bank_reconciliation')
  if (isAgerasPlan) {
    features.map((feature) => {
      feature.checkboxStyle =
        feature.checkboxStyle === SubscriptionFeatureCheckboxType.DEEP_SEA_GREEN
          ? SubscriptionFeatureCheckboxType.ELECTRIC_GREEN
          : feature.checkboxStyle
    })

    if (features && !features.some((item) => item.id === QuotaTypes.E_INVOICE)) {
      features.splice(2, 0, {
        id: QuotaTypes.E_INVOICE,
        type: SubscriptionFeatureType.INVOICING,
        translationKeyMonth: keys.eInvoice,
        translationKeyYear: keys.eInvoice,
        checkboxStyle: SubscriptionFeatureCheckboxType.DASH,
      })
    }

    if (features && !features.some((item) => item.id === QuotaTypes.INVOICE)) {
      features.splice(3, 0, {
        id: QuotaTypes.INVOICE,
        type: SubscriptionFeatureType.INVOICING,
        translationKeyMonth: keys.premiumInvoicesPerMonth,
        translationKeyYear: keys.premiumInvoicesPerYear,
        checkboxStyle: SubscriptionFeatureCheckboxType.DASH,
      })
    }
  }

  const groupedFeatures = groupBy(features, 'type')

  const agerasPlanSuffix = (feature: ISubscriptionFeatureItem) => {
    const quotaTypes = [
      QuotaTypes.INVOICE,
      QuotaTypes.CUSTOMERS,
      QuotaTypes.LIMITED_INVOICES_ESTIMATES,
      QuotaTypes.INVOICES_ESTIMATES,
    ]
    return isAgerasPlan && feature.id && quotaTypes.includes(feature.id) ? '.ageras' : ''
  }

  return (
    <div className={cls({ ...{ __features: true }, ...classProps })}>
      {isAgerasPlan ? (
        <>
          {groupedFeatures.invoicing.length && (
            <div className={cls('__featureType__invoicing')}>
              <div className={cls('__featureType__invoicing__title')}>
                {l(keys.premiumPlanModal.featuresGroup.invoicing)}
              </div>
              {groupedFeatures.invoicing.map((feature: ISubscriptionFeatureItem) => (
                <div key={feature.id || feature.translationKeyMonth} className={cls('__feature')}>
                  <FeatureCheckmark feature={feature} />
                  <div className={cls('__featureType__invoicing__text')}>
                    {feature.amount === -1 ? (
                      <div className={cls('__featureType__invoicing__text__infinity')} />
                    ) : (
                      <>{feature.amount !== undefined && feature.amount !== 0 && <div>{feature.amount}</div>}</>
                    )}
                    {feature &&
                      l(
                        billingCycle === 'year'
                          ? `${feature.translationKeyYear}${agerasPlanSuffix(feature)}`
                          : `${feature.translationKeyMonth}${agerasPlanSuffix(feature)}`,
                      )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {groupedFeatures.accounting.length && (
            <div className={cls('__featureType__accounting')}>
              <div className={cls('__featureType__accounting__title')}>
                {l(keys.premiumPlanModal.featuresGroup.accounting)}
              </div>
              {groupedFeatures.accounting.map((feature: ISubscriptionFeatureItem) => (
                <div key={feature.id || feature.translationKeyMonth} className={cls('__feature')}>
                  <FeatureCheckmark feature={feature} />
                  <div className={cls('__featureType__accounting__text')}>
                    {feature.amount === -1 ? (
                      <div className={cls('__featureType__accounting__text__infinity')} />
                    ) : (
                      <>{feature.amount !== undefined && feature.amount !== 0 && <div>{feature.amount}</div>}</>
                    )}
                    {feature &&
                      l(
                        billingCycle === 'year'
                          ? `${feature.translationKeyYear}${agerasPlanSuffix(feature)}`
                          : `${feature.translationKeyMonth}${agerasPlanSuffix(feature)}`,
                      )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {groupedFeatures.admin.length && (
            <div className={cls('__featureType__admin')}>
              <div className={cls('__featureType__admin__title')}>{l(keys.premiumPlanModal.featuresGroup.admin)}</div>
              {groupedFeatures.admin.map((feature: ISubscriptionFeatureItem) => (
                <div key={feature.id || feature.translationKeyMonth} className={cls('__feature')}>
                  <FeatureCheckmark feature={feature} />
                  <div className={cls('__featureType__admin__text')}>
                    {feature.amount === -1 ? (
                      <div className={cls('__featureType__admin__text__infinity')} />
                    ) : (
                      <>{feature.amount !== undefined && feature.amount !== 0 && <div>{feature.amount}</div>}</>
                    )}
                    {feature &&
                      l(
                        billingCycle === 'year'
                          ? `${feature.translationKeyYear}${agerasPlanSuffix(feature)}`
                          : `${feature.translationKeyMonth}${agerasPlanSuffix(feature)}`,
                      )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        <>
          {features &&
            features.map((feature) => (
              <div key={feature.id || feature.translationKeyMonth} className={cls('__feature')}>
                {feature.amount !== undefined ? (
                  <InvoiceQuota
                    selectedPlanName={selectedPlanName}
                    isCombinedPlan={isCombinedPlan}
                    billingCycle={billingCycle}
                    feature={feature}
                    onSelectCombinedPlan={onSelectCombinedPlan}
                    selectableAvailablePlans={selectableAvailablePlans}
                    allPlans={allPlans}
                    processingPlanName={processingPlanName}
                  />
                ) : (
                  <FeatureCheckmark feature={feature} />
                )}
                {
                  // eslint-disable-next-line no-nested-ternary
                  feature.id === 'invoices_estimates' ? (
                    <>
                      <div className={cls('__text_invoices_estimates')}>
                        {l(billingCycle === 'year' ? feature.translationKeyYear : feature.translationKeyMonth)}
                      </div>
                      <div className={cls('__text_invoices')}>{l('subscription.invoices_estimates_text')}</div>
                    </>
                  ) : // eslint-disable-next-line no-nested-ternary
                  feature.id === 'premium_customers' ? (
                    <div className={cls('__text_premium_customers')}>
                      {l(billingCycle === 'year' ? feature.translationKeyYear : feature.translationKeyMonth)}
                    </div>
                  ) : // eslint-disable-next-line no-nested-ternary
                  feature.id === 'premium_invoice' ? (
                    <div className={cls('__text_premium_invoice')}>
                      {l(billingCycle === 'year' ? feature.translationKeyYear : feature.translationKeyMonth)}
                    </div>
                  ) : feature.id === 'limited_invoices_estimates' ? (
                    <div className={cls('__text_premium_invoice')}>{l('subscription.invoices_estimates_text')}</div>
                  ) : (
                    <div className={cls('__text')}>
                      {l(billingCycle === 'year' ? feature.translationKeyYear : feature.translationKeyMonth)}
                    </div>
                  )
                }
              </div>
            ))}
        </>
      )}
    </div>
  )
}

export default FeaturesSection
